import {
  CalendarDays,
  Clock,
  MapPin,
  Armchair,
  Ticket,
  CheckCircle2,
  ChevronRight,
  Settings2,
} from "lucide-react";
import EventoImagen from "@/components/common/EventoImagen";
import { MIS_BOLETOS } from "@/constants/testIds";
import { formatearFechaLarga } from "@/utils/fechas";

/**
 * Tarjeta consolidada de un evento dentro de "Mis Boletos" (Sprint 54).
 * Agrupa todas las compras CONFIRMADAS del mismo evento. Muestra totales
 * acumulados y dos acciones: administrar todos los boletos o ver el
 * desglose por compra.
 */
export default function EventoBoletosCard({ grupo, onAdministrar, onVerDetalles }) {
  const fmt = (n) => `$${Number(n).toLocaleString("es-MX")} MXN`;
  const eid = grupo.eventoId;
  const asignadosTodos =
    grupo.totalBoletosReales > 0 && grupo.pendientes === 0;

  const salonesLabel =
    grupo.salones.length > 1
      ? `${grupo.salones.length} salones`
      : grupo.salones[0] || "—";
  const mesasLabel =
    grupo.mesas.length > 1
      ? `${grupo.mesas.length} mesas`
      : grupo.mesas.length === 1
        ? `Mesa ${grupo.mesas[0]}`
        : "—";

  return (
    <article
      data-testid={MIS_BOLETOS.eventoCard(eid)}
      className="group rounded-2xl border hairline bg-card overflow-hidden shadow-soft transition-shadow duration-300 motion-reduce:transition-none hover:shadow-elevated flex flex-col"
    >
      <div className="relative aspect-[16/9] w-full overflow-hidden bg-muted">
        <EventoImagen
          src={grupo.imagen}
          eventoId={grupo.eventoId}
          alt={grupo.nombreEvento}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="p-6 flex flex-col gap-4 flex-1">
        <div className="flex flex-wrap items-center justify-center gap-2">
          <span className="inline-flex items-center justify-center gap-1.5 w-[9.5rem] px-3 py-1 rounded-full text-[11px] font-medium text-center border border-emerald-500/40 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 className="w-3 h-3" strokeWidth={2} />
            {grupo.compras.length > 1
              ? `${grupo.compras.length} compras`
              : "Compra confirmada"}
          </span>
          {grupo.simulado && (
            <span className="inline-flex items-center justify-center w-[9.5rem] px-3 py-1 rounded-full text-[11px] font-medium text-center border border-amber-500/40 bg-amber-500/15 text-amber-700 dark:text-amber-300">
              Operación de prueba
            </span>
          )}
        </div>

        <h3
          data-testid={MIS_BOLETOS.eventoNombre(eid)}
          className="font-heading text-xl text-foreground leading-snug"
        >
          {grupo.nombreEvento}
        </h3>

        <ul className="flex flex-col gap-2 text-sm text-muted-foreground">
          <li className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-accent" strokeWidth={1.6} />
            <span>{formatearFechaLarga(grupo.fechaEvento)}</span>
          </li>
          <li className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-accent" strokeWidth={1.6} />
            <span>{grupo.horaEvento}</span>
          </li>
          <li className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-accent" strokeWidth={1.6} />
            <span
              data-testid={MIS_BOLETOS.eventoSalones(eid)}
              title={grupo.salones.join(" · ")}
            >
              {salonesLabel}
            </span>
          </li>
          {grupo.mesas.length > 0 && (
            <li className="flex items-center gap-2">
              <Armchair className="w-4 h-4 text-accent" strokeWidth={1.6} />
              <span title={grupo.mesas.map((m) => `Mesa ${m}`).join(" · ")}>
                {mesasLabel}
              </span>
            </li>
          )}
          <li className="flex items-center gap-2">
            <Ticket className="w-4 h-4 text-accent" strokeWidth={1.6} />
            <span data-testid={MIS_BOLETOS.eventoTotalBoletos(eid)}>
              {grupo.totalBoletos}{" "}
              {grupo.totalBoletos === 1 ? "boleto" : "boletos"}
            </span>
          </li>
        </ul>

        <div className="pt-3 border-t hairline flex items-center justify-between">
          <span className="overline text-muted-foreground">Total pagado</span>
          <span
            data-testid={MIS_BOLETOS.eventoTotalPagado(eid)}
            className="font-heading text-lg text-primary dark:text-accent"
          >
            {fmt(grupo.totalPagado)}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3 text-center">
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2">
            <div
              data-testid={MIS_BOLETOS.eventoAsignados(eid)}
              className="font-heading text-xl text-emerald-700 dark:text-emerald-300"
            >
              {grupo.asignados}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
              asignados
            </div>
          </div>
          <div
            className={
              "rounded-xl border px-3 py-2 " +
              (grupo.pendientes === 0
                ? "border-border bg-muted"
                : "border-amber-500/30 bg-amber-500/10")
            }
          >
            <div
              data-testid={MIS_BOLETOS.eventoPendientes(eid)}
              className={
                "font-heading text-xl " +
                (grupo.pendientes === 0
                  ? "text-muted-foreground"
                  : "text-amber-700 dark:text-amber-300")
              }
            >
              {grupo.pendientes}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
              pendientes
            </div>
          </div>
        </div>

        <div className="mt-auto flex flex-col gap-2">
          <button
            type="button"
            data-testid={MIS_BOLETOS.btnAdministrar(eid)}
            onClick={onAdministrar}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold bg-primary text-primary-foreground dark:bg-accent dark:text-accent-foreground ring-1 ring-primary/20 dark:ring-accent/40 shadow-soft transition-all duration-200 active:scale-[0.99] hover:opacity-95"
          >
            <Settings2 className="w-4 h-4" strokeWidth={1.8} />
            {asignadosTodos ? "Administrar boletos" : "Asignar boletos"}
          </button>
          <button
            type="button"
            data-testid={MIS_BOLETOS.btnVerDetalles(eid)}
            onClick={onVerDetalles}
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-medium border hairline bg-card text-foreground hover:bg-muted transition-colors"
          >
            Ver detalles
            <ChevronRight className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>
      </div>
    </article>
  );
}
